import React, { useMemo } from "react";
import { MessageSquareQuote, CirclePlus, CirclePlay, Trash2, Clock, CirclePause, Edit } from "lucide-react";
import { Table, type TableColumn } from "equal-ds-ui";
import { Button } from 'equal-ds-ui';
import type { NudgeItem } from "../../../types/nudges";

interface Props {
  nudges: NudgeItem[];
  isLoading: boolean;
  isExecutingNudge: string | null;
  isDeletingNudge: string | null;
  onExecuteNudge: (nudgeId: string) => Promise<void>;
  onDeleteNudge: (nudgeId: string) => Promise<void>;
  onEditNudge: (nudge: NudgeItem) => void;
  onCreateNudge: () => void;
}

export const NudgesTable: React.FC<Props> = ({
  nudges,
  isLoading,
  isExecutingNudge,
  isDeletingNudge,
  onExecuteNudge,
  onDeleteNudge,
  onEditNudge,
  onCreateNudge,
}) => {
  const columns: TableColumn<NudgeItem>[] = useMemo(
    () => [
      {
        key: "details",
        header: "NUDGE DETAILS",
        width: 320,
        locked: true,
        render: (_, row) => (
          <div className="flex flex-col gap-1">
            <div className="text-[15px] font-semibold text-[#2b2c30]">
              {row.nudge_name}
            </div>

            <div className="text-xs text-[#6b6c70] leading-snug max-w-[320px] whitespace-normal break-words">
              {row.message_template}
            </div>
          </div>
        ),
      },

      {
        key: "type",
        header: "TYPE",
        width: 240,
        render: (_, row) => {
          const config = {
            ON_DEMAND: {
              label: "ON-DEMAND",
              style: "bg-[#f7f3fa] text-[#715983]",
            },
            SCHEDULED: {
              label: "SCHEDULED",
              style: "bg-[#fbf6ef] text-[#a36f35]",
            },
            RECURRING: {
              label: "RECURRING",
              style: "bg-[#fbf6ef] text-[#a36f35]",
            },
          }[row.notification_type];

          return (
            <div className="flex items-center gap-2 py-1">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.style} shadow-sm`}>
                {config.label}
              </span>

              {row.notification_type === "SCHEDULED" &&
                row.scheduled_date_time && (
                  <div className="flex items-center gap-1 text-[#666a70] text-xs font-medium">
                    <Clock strokeWidth={1.7} size={14} />
                    {(() => {
                      const date = new Date(row.scheduled_date_time);
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${hours}:${minutes}, ${day}/${month}/${year}`;
                    })()}
                  </div>
                )}
            </div>
          );
        },
      },

      {
        key: "targeting",
        header: "TARGETING",
        width: 150,
        render: (_, row) => {
          const config = {
            QUERY_BASED: {
              label: "QUERY",
              style:
                "bg-[#eef4fe] text-[#315d99]",
            },
            DIRECT_USER_IDS: {
              label: "DIRECT",
              style:
                "bg-[#edf8f0] text-[#2f7046]",
            },
          }[row.targeting_mode];

          return (
            <div className="flex items-center gap-2 py-1">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${config.style} shadow-sm`}
              >
                {config.label}
              </span>
            </div>
          );
        },
      },
      {
        key: "status",
        header: "STATUS",
        width: 150,
        render: (_, row) => {
          const config = {
            ACTIVE: {
              label: "ACTIVE",
              style: "bg-[#edf8f0] text-[#2f7046]",
            },
            COMPLETED: {
              label: "COMPLETED",
              style: "bg-[#fbf6ef] text-[#a36f35]",
            },
            DELETED: {
              label: "DELETED",
              style: "bg-[#ffebee] text-[#c62828]",
            },
            ERROR: {
              label: "ERROR",
              style: "bg-[#fff3e0] text-[#e65100]",
            },
            INACTIVE: {
              label: "INACTIVE",
              style: "bg-[#f5f5f5] text-[#616161]",
            },
            DRAFT: {
              label: "DRAFT",
              style: "bg-[#e3f2fd] text-[#1565c0]",
            },
            SCHEDULED: {
              label: "SCHEDULED",
              style: "bg-[#fff9c4] text-[#f57f17]",
            },
            FAILED: {
              label: "FAILED",
              style: "bg-[#ffebee] text-[#d32f2f]",
            },
          }[row.status || 'ACTIVE'];

          return (
            <div className="flex items-center gap-2 py-1">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${config?.style || 'bg-gray-100 text-gray-700'} shadow-sm`}
              >
                {config?.label || row.status || 'ACTIVE'}
              </span>
            </div>
          );
        },
      },
      {
        key: "created_on",
        header: "CREATED ON",
        width: 200,
        render: (_, row) => {
          const date = new Date(row.created_at);
          return (
            <span className="text-sm text-[#3d3e40]">
              {date.toLocaleString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour12: false,
              })}
            </span>
          );
        },
      },

      {
        key: "actions",
        header: "ACTIONS",
        width: 120,
        align: "center",
        isActions: false,
        render: (_, row) => {
          // Disable all actions if any operation is in progress for this row
          const isOperationInProgress = isExecutingNudge === row.nudge_id || isDeletingNudge === row.nudge_id;
          // Disable all actions if any other operation is in progress
          const isAnyOperationInProgress = isExecutingNudge !== null || isDeletingNudge !== null;

          return (
            <div className="flex justify-center items-center gap-2">
              {/* execute */}
              <button
                disabled={isAnyOperationInProgress}
                onClick={() => onExecuteNudge(row.nudge_id)}
                className="p-[6px] border rounded-full text-[#3568dc] hover:bg-[#ebf3ff] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isExecutingNudge === row.nudge_id ? (
                  <CirclePause size={20} className="animate-spin" />
                ) : (
                  <CirclePlay size={20} />
                )}
              </button>

              {/* edit */}
              <button
                disabled={isOperationInProgress}
                onClick={() => onEditNudge(row)}
                className="p-[6px] border rounded-full text-[#4d6f52] hover:bg-[#eff8f0] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Edit size={20} />
              </button>

              {/* delete */}
              <button
                disabled={isAnyOperationInProgress}
                onClick={() => onDeleteNudge(row.nudge_id)}
                className="p-[6px] border rounded-full text-[#ca4545] hover:bg-[#fdecec] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {isDeletingNudge === row.nudge_id ? (
                  <CirclePause size={20} className="animate-spin" />
                ) : (
                  <Trash2 size={20} />
                )}
              </button>
            </div>
          );
        },
      },
    ],
    [
      isExecutingNudge,
      isDeletingNudge,
      onExecuteNudge,
      onDeleteNudge,
      onEditNudge,
    ]
  );

  return (
    <div className="flex flex-col gap-4 p-1 flex-1">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl border border-blue-200">
              <MessageSquareQuote className="w-6 h-6 text-text-primary" />
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
              All Nudges
            </h2>
          </div>

          <Button variant="primary" size="sm" onClick={onCreateNudge}>
            <CirclePlus className="w-5 h-5 mr-2" />
            New Nudge
          </Button>
        </div>

        <Table<NudgeItem>
          className="rounded-xl shadow-sm"
          columns={columns}
          data={nudges}
          loading={isLoading}
          loadingText="Loading nudges..."
          emptyText="No nudges found. Create your first nudge to get started."
        />
      </div>
    </div>
  );
};
